// src/config/apiBase.ts
// ============================================================
// THE API BASE — ONE RESOLVER, AND A HARD STATIC-DEMO SWITCH
//
// Every backend URL in the app resolves through this module. There are no `|| 'http://
// localhost:8080'` fallbacks left anywhere, and no hardcoded hosts.
//
// THE RULE:
//   VITE_API_URL set    → API_BASE is that origin; the app talks to it normally.
//   VITE_API_URL EMPTY  → STATIC DEMO MODE: the app makes NO backend network calls at all.
//                         Seeded fixture data is the truth. apiFetch() refuses to fire.
//
// WHY THIS EXISTS. The published demo was built with a developer's .env, so
// `VITE_API_URL=http://localhost:8080` was baked as a literal into the public JavaScript.
// Every visitor's browser was therefore told to fetch data from port 8080 *on their own
// machine*. For almost everyone that silently failed and the seeds rendered — fine by
// accident. But on a machine that happened to be running the DVA backend, those calls
// SUCCEEDED, and a half-populated server row plus a server-side strength score overrode the
// authored demo state: the profile page showed 64% "Fair" with three "needs attention"
// banners while the form visibly displayed all three of those fields filled in.
//
// Blanking the base is not enough on its own: a relative fetch('/api/...') would still hit
// the page's own origin and 404. So static mode has to REFUSE to make the call, which is
// what apiFetch does.
//
// Callers do not need a special path for static mode. apiFetch rejects with a
// StaticDemoError, which flows into the try/catch every one of these call sites already has
// and lands on its existing seed/fallback branch.
// ============================================================

/** Raw configured origin, trailing slashes stripped. Empty string means static demo. */
export const API_BASE: string = (import.meta.env.VITE_API_URL ?? '')
  .trim()
  .replace(/\/+$/, '');

/** Sessions/auth service — separate origin, same rule. */
export const SESSIONS_API_BASE: string = (import.meta.env.VITE_SESSIONS_API_URL ?? '')
  .trim()
  .replace(/\/+$/, '');

/**
 * True when no backend is configured. In this mode the app is entirely self-contained:
 * fixtures and demo seeds are the source of truth and nothing is fetched.
 */
export const IS_STATIC_DEMO: boolean = API_BASE === '';

/** Thrown instead of performing a request when the app is in static-demo mode. */
export class StaticDemoError extends Error {
  readonly isStaticDemo = true;
  constructor(path = '') {
    super(
      `Static demo mode: no backend configured (VITE_API_URL is empty), skipped request to ${path || 'the API'}.`,
    );
    this.name = 'StaticDemoError';
  }
}

export function isStaticDemoError(err: unknown): boolean {
  return Boolean(err && typeof err === 'object' && (err as { isStaticDemo?: boolean }).isStaticDemo);
}

/** Join the configured base with a path. Throws in static mode — call sites should use apiFetch. */
export function apiUrl(path: string, base: string = API_BASE): string {
  if (!base) throw new StaticDemoError(path);
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * The ONLY way the app should reach the backend.
 * In static-demo mode this rejects without touching the network.
 *
 * Accepts either a path ('/api/v2/...') or an already-built absolute URL, so existing call
 * sites that interpolate API_BASE into a template literal can adopt it by renaming `fetch`
 * to `apiFetch` — no URL rewriting, no behaviour change when a backend IS configured.
 */
export function apiFetch(pathOrUrl: string, init?: RequestInit): Promise<Response> {
  if (IS_STATIC_DEMO) return Promise.reject(new StaticDemoError(pathOrUrl));
  const url = /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : apiUrl(pathOrUrl);
  return fetch(url, init);
}

/** Same, for the sessions/auth origin. */
export function sessionsFetch(path: string, init?: RequestInit): Promise<Response> {
  if (!SESSIONS_API_BASE) return Promise.reject(new StaticDemoError(path));
  return fetch(apiUrl(path, SESSIONS_API_BASE), init);
}

/** Dev-only diagnostic note. No-ops in production builds. */
export function devNote(...args: unknown[]): void {
  if (import.meta.env.DEV) console.info(...(args as []));
}
